import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { ChoreAssignment, FreshStartResult } from '@chore-project/shared';
import { AssignmentStatus } from '@chore-project/shared';
import { useActiveMemberContext } from '../context/ActiveMemberContext';

export function useMyAssignments(status?: AssignmentStatus) {
  const { activeMemberId } = useActiveMemberContext();
  return useQuery<ChoreAssignment[]>({
    // Acting-as identity is part of the key so the primary's and a shared user's
    // dashboards don't share a cache entry.
    queryKey: ['my-assignments', status, activeMemberId],
    queryFn: async () => {
      const url = status ? `/me/assignments?status=${status}` : '/me/assignments';
      const { data } = await apiClient.get<ChoreAssignment[]>(url);
      return data;
    },
  });
}

export function useChoreAssignments(
  householdId: string,
  filters?: {
    status?: AssignmentStatus | AssignmentStatus[];
    assigneeId?: string;
    from?: string;
    to?: string;
  },
) {
  return useQuery<ChoreAssignment[]>({
    queryKey: ['chore-assignments', householdId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) {
        params.set('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
      }
      if (filters?.assigneeId) params.set('assigneeId', filters.assigneeId);
      // Inclusive dueDate window — scopes the query server-side to the date range.
      if (filters?.from) params.set('from', filters.from);
      if (filters?.to) params.set('to', filters.to);
      const { data } = await apiClient.get<ChoreAssignment[]>(
        `/households/${householdId}/chore-assignments?${params.toString()}`,
      );
      return data;
    },
    enabled: !!householdId,
  });
}

/** Completed assignments for the current user across all households that are due today. */
export function useMyTodayCompleted(userId: string) {
  const { activeMemberId } = useActiveMemberContext();
  return useQuery<ChoreAssignment[]>({
    queryKey: ['my-assignments', AssignmentStatus.COMPLETED, 'today', userId, activeMemberId],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0] ?? '';
      const { data } = await apiClient.get<ChoreAssignment[]>(
        `/me/assignments?status=${AssignmentStatus.COMPLETED}`,
      );
      return data.filter((a) => a.dueDate === today);
    },
    enabled: !!userId,
  });
}

export interface MemberDayStat {
  pending: number;
  completed: number;
  overdue: number;
}

/** Fetch all assignments for a household and build per-member stats (today + overdue). */
export function useTodayMemberStats(householdId: string) {
  return useQuery<Record<string, MemberDayStat>>({
    queryKey: ['chore-assignments', householdId, 'today-stats'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0] ?? '';
      const { data } = await apiClient.get<ChoreAssignment[]>(
        `/households/${householdId}/chore-assignments`,
      );
      const stats: Record<string, MemberDayStat> = {};
      for (const a of data) {
        if (!stats[a.assigneeId]) stats[a.assigneeId] = { pending: 0, completed: 0, overdue: 0 };
        if (a.dueDate === today) {
          if (a.status === AssignmentStatus.PENDING) stats[a.assigneeId].pending++;
          else if (a.status === AssignmentStatus.COMPLETED) stats[a.assigneeId].completed++;
        } else if (a.dueDate < today && a.status === AssignmentStatus.PENDING) {
          stats[a.assigneeId].overdue++;
        }
      }
      return stats;
    },
    enabled: !!householdId,
  });
}

export function useCreateAssignment() {
  const queryClient = useQueryClient();
  return useMutation<
    ChoreAssignment,
    Error,
    { householdId: string; choreDefId: string; assigneeId: string; dueDate: string }
  >({
    // The target household rides with each call so a one-off task can be
    // created in whichever household the chosen chore belongs to.
    mutationFn: async ({ householdId, ...dto }) => {
      const { data } = await apiClient.post<ChoreAssignment>(
        `/households/${householdId}/chore-assignments`,
        dto,
      );
      return data;
    },
    onSuccess: (_, { householdId }) => {
      void queryClient.invalidateQueries({ queryKey: ['chore-assignments', householdId] });
      void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
    },
  });
}

export function useCompleteAssignment() {
  const queryClient = useQueryClient();
  return useMutation<
    ChoreAssignment,
    Error,
    { householdId: string; dueDate: string; assignmentId: string }
  >({
    mutationFn: async ({ householdId, dueDate, assignmentId }) => {
      const { data } = await apiClient.patch<ChoreAssignment>(
        `/households/${householdId}/chore-assignments/${dueDate}/${assignmentId}/complete`,
      );
      return data;
    },
    onSuccess: (_, { householdId }) => {
      void queryClient.invalidateQueries({ queryKey: ['chore-assignments', householdId] });
      void queryClient.invalidateQueries({ queryKey: ['my-assignments'] }); // invalidates all my-assignments/* queries
      // Refresh ALL points queries (daily, range, leaderboard, across households) —
      // the dashboard hero/streak reads the 'range' query, which must update too.
      void queryClient.invalidateQueries({ queryKey: ['points'] });
    },
  });
}

/**
 * "Fresh start" reset — clears the user's backlog (skips all open assignments due
 * on/before today) and re-creates today's chores, leaving a stress-free state.
 */
export function useFreshStart() {
  const queryClient = useQueryClient();
  return useMutation<FreshStartResult, Error, void>({
    mutationFn: async () => {
      const { data } = await apiClient.post<FreshStartResult>('/me/fresh-start');
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['chore-assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['points'] });
    },
  });
}

export function useSkipAssignment() {
  const queryClient = useQueryClient();
  return useMutation<
    ChoreAssignment,
    Error,
    { householdId: string; dueDate: string; assignmentId: string }
  >({
    mutationFn: async ({ householdId, dueDate, assignmentId }) => {
      const { data } = await apiClient.patch<ChoreAssignment>(
        `/households/${householdId}/chore-assignments/${dueDate}/${assignmentId}/skip`,
      );
      return data;
    },
    onSuccess: (_, { householdId }) => {
      void queryClient.invalidateQueries({ queryKey: ['chore-assignments', householdId] });
      void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['points'] });
    },
  });
}

/** Chores other members have passed to the current user, awaiting accept/decline. */
export function useIncomingShares() {
  const { activeMemberId } = useActiveMemberContext();
  return useQuery<ChoreAssignment[]>({
    queryKey: ['incoming-shares', activeMemberId],
    queryFn: async () => {
      const { data } = await apiClient.get<ChoreAssignment[]>('/me/incoming-shares');
      return data;
    },
  });
}

type ShareVars = { householdId: string; dueDate: string; assignmentId: string };

/** Shared invalidation for any share state change. */
function useShareInvalidation() {
  const queryClient = useQueryClient();
  return (householdId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['chore-assignments', householdId] });
    void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
    void queryClient.invalidateQueries({ queryKey: ['incoming-shares'] });
    void queryClient.invalidateQueries({ queryKey: ['points'] });
  };
}

/** Pass a chore to another household member. */
export function useShareAssignment() {
  const invalidate = useShareInvalidation();
  return useMutation<ChoreAssignment, Error, ShareVars & { recipientId: string }>({
    mutationFn: async ({ householdId, dueDate, assignmentId, recipientId }) => {
      const { data } = await apiClient.patch<ChoreAssignment>(
        `/households/${householdId}/chore-assignments/${dueDate}/${assignmentId}/share`,
        { recipientId },
      );
      return data;
    },
    onSuccess: (_, { householdId }) => invalidate(householdId),
  });
}

function useShareTransition(action: 'cancel-share' | 'accept-share' | 'decline-share') {
  const invalidate = useShareInvalidation();
  return useMutation<ChoreAssignment, Error, ShareVars>({
    mutationFn: async ({ householdId, dueDate, assignmentId }) => {
      const { data } = await apiClient.patch<ChoreAssignment>(
        `/households/${householdId}/chore-assignments/${dueDate}/${assignmentId}/${action}`,
      );
      return data;
    },
    onSuccess: (_, { householdId }) => invalidate(householdId),
  });
}

/** Owner cancels an outstanding offer. */
export function useCancelShare() {
  return useShareTransition('cancel-share');
}

/** Recipient accepts a passed chore and becomes its owner. */
export function useAcceptShare() {
  return useShareTransition('accept-share');
}

/** Recipient declines a passed chore, returning it to the original owner. */
export function useDeclineShare() {
  return useShareTransition('decline-share');
}
